import React, { useState, useCallback, useRef, memo } from 'react';
import {
  Card,
  Input,
  Select,
  Typography,
  Divider,
  message,
  Alert,
  Space,
  Button,
} from 'antd';
import {
  CarOutlined,
  UserOutlined,
  PhoneOutlined,
  ToolOutlined,
} from '@ant-design/icons';
import { WorkOrder } from '../../types';
import { assetLabel } from '../../utils/assetLabel';
import PhotoStrip from '../Shared/PhotoStrip';
import CameraCaptureField from '../Shared/CameraCaptureField';
import { InternalNotesField } from '../Shared/InternalNotesField';
import { TaxExemptionToggle } from '../Shared/TaxExemptionToggle';
import TechnicianSelect from '../Shared/TechnicianSelect';
import { useCapability } from '../auth/RoleGuard';
import { QuickBookingDrawer } from '../SchedulingHub/QuickBookingDrawer';

const { Text } = Typography;

export interface WorkOrderFormFieldsProps {
  workOrder: WorkOrder;
  isMobile: boolean;
  isLocked: boolean;
  onFieldSave: (field: string, value: any) => Promise<void>;
  /** Optional multi-field save (single PUT) — used by the tech picker to persist
   *  assigned_technician_id + assigned_technician together. Falls back to
   *  sequential onFieldSave calls when absent. */
  onFieldsSave?: (fields: Record<string, any>) => Promise<void>;
  setIsEditing: (editing: boolean) => void;
  /** Optional: called when tax rate should change (e.g. tax-exempt customer detected) */
  onTaxRateChange?: (rate: number) => void;
  /** Optional: notified when internal notes change locally */
  onInternalNotesChange?: (notes: string) => void;
}

interface InlineEditFieldProps {
  value: string;
  onSave: (value: string) => Promise<void>;
  label: string;
  isLocked: boolean;
  multiline?: boolean;
  setIsEditing: (editing: boolean) => void;
}

const InlineEditField: React.FC<InlineEditFieldProps> = ({
  value,
  onSave,
  label,
  isLocked,
  multiline = false,
  setIsEditing,
}) => {
  const [editing, setEditingLocal] = useState(false);
  const [draft, setDraft] = useState(value);
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const startEdit = useCallback(() => {
    if (isLocked) return;
    setDraft(value);
    setEditingLocal(true);
    setIsEditing(true);
  }, [isLocked, value, setIsEditing]);

  const handleSave = useCallback(async () => {
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(async () => {
      try {
        if (draft !== value) {
          await onSave(draft);
        }
      } catch {
        message.error('Could not save changes. Check your connection and try again.');
      } finally {
        setEditingLocal(false);
        setIsEditing(false);
      }
    }, 500);
  }, [draft, value, onSave, setIsEditing]);

  if (!editing) {
    return (
      <div
        onClick={startEdit}
        style={{
          cursor: isLocked ? 'default' : 'pointer',
          minHeight: 48,
          padding: '8px 0',
        }}
      >
        <Text type="secondary" style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</Text>
        <div style={{ fontSize: 14, fontWeight: 500, color: value ? '#111827' : '#9ca3af', marginTop: 2 }}>
          {value || 'Tap to edit'}
        </div>
      </div>
    );
  }

  if (multiline) {
    return (
      <div style={{ padding: '8px 0' }}>
        <Text type="secondary" style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</Text>
        <Input.TextArea
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onBlur={handleSave}
          autoFocus
          autoSize={{ minRows: 2, maxRows: 6 }}
          style={{ fontSize: 16, marginTop: 4 }}
        />
      </div>
    );
  }

  return (
    <div style={{ padding: '8px 0' }}>
      <Text type="secondary" style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</Text>
      <Input
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={handleSave}
        autoFocus
        style={{ fontSize: 16, marginTop: 4 }}
      />
    </div>
  );
};

const STATUS_OPTIONS: { value: WorkOrder['status']; label: string }[] = [
  { value: 'new', label: 'New' },
  { value: 'created', label: 'Created' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'waiting_parts', label: 'Waiting Parts' },
  { value: 'waiting_approval', label: 'Waiting Approval' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
];

const WorkOrderFormFields: React.FC<WorkOrderFormFieldsProps> = ({
  workOrder,
  isMobile,
  isLocked,
  onFieldSave,
  onFieldsSave,
  setIsEditing,
  onTaxRateChange,
  onInternalNotesChange,
}) => {
  const [editingStatus, setEditingStatus] = useState(false);
  // Bump on photo upload so the condition strip re-fetches immediately.
  const [conditionReloadKey, setConditionReloadKey] = useState(0);
  const canManageSchedule = useCapability('manage_schedule');
  // Dismissible "Book a visit with {name}?" prompt shown after assigning a
  // technician — purely optional, never blocks or undoes the assignment.
  const [bookPromptTech, setBookPromptTech] = useState<{ id: number; name: string } | null>(null);
  const [bookingOpen, setBookingOpen] = useState(false);
  // @ts-ignore TS2339: internal_notes exists at runtime but not in WorkOrder type — pre-existing gap
  const [internalNotes, setInternalNotes] = useState((workOrder as any).internal_notes || '');

  const handleInternalNotesBlur = useCallback(async () => {
    if (internalNotes !== ((workOrder as any).internal_notes || '')) {
      try {
        await onFieldSave('internal_notes', internalNotes);
        onInternalNotesChange?.(internalNotes);
      } catch {
        message.error('Could not save internal notes.');
      }
    }
  }, [internalNotes, (workOrder as any).internal_notes, onFieldSave, onInternalNotesChange]);

  const handleStatusChange = useCallback(async (newStatus: WorkOrder['status']) => {
    try {
      await onFieldSave('status', newStatus);
      message.success('Status updated');
    } catch {
      message.error('Failed to update status');
    } finally {
      setEditingStatus(false);
      setIsEditing(false);
    }
  }, [onFieldSave, setIsEditing]);

  const handleFieldSave = useCallback((field: string) => async (value: string) => {
    await onFieldSave(field, value);
  }, [onFieldSave]);

  // Tech picker: persist the users.id FK (drives labor cost in owner P&L)
  // together with the display name so both columns stay coherent.
  const handleTechnicianChange = useCallback(async (techId: number | null, techName: string | null) => {
    try {
      if (onFieldsSave) {
        await onFieldsSave({ assigned_technician_id: techId, assigned_technician: techName });
      } else {
        await onFieldSave('assigned_technician_id', techId);
        await onFieldSave('assigned_technician', techName);
      }
      message.success(techId != null ? 'Technician assigned' : 'Technician unassigned');
      if (techId != null && canManageSchedule) {
        setBookPromptTech({ id: techId, name: techName || 'this technician' });
      } else {
        setBookPromptTech(null);
      }
    } catch {
      message.error('Failed to update technician');
    }
  }, [onFieldSave, onFieldsSave, canManageSchedule]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 12 : 16 }}>
      {/* Status Edit */}
      <Card className="shadow-sm" styles={{ body: { padding: isMobile ? '14px' : '16px' } }} style={{ overflow: 'hidden' }}>
        <div style={{ minHeight: 48 }}>
          <Text type="secondary" style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: 4 }}>
            Status
          </Text>
          {editingStatus && !isLocked ? (
            <Select
              value={workOrder.status}
              onChange={handleStatusChange}
              onBlur={() => { setEditingStatus(false); setIsEditing(false); }}
              options={STATUS_OPTIONS}
              autoFocus
              open
              style={{ width: '100%', fontSize: 16 }}
              size="large"
            />
          ) : (
            <div
              onClick={() => {
                if (!isLocked) {
                  setEditingStatus(true);
                  setIsEditing(true);
                }
              }}
              style={{
                cursor: isLocked ? 'default' : 'pointer',
                fontSize: 14,
                fontWeight: 500,
                padding: '4px 0',
              }}
            >
              {workOrder.status.replace(/_/g, ' ').toUpperCase()}
            </div>
          )}
        </div>
      </Card>

      {/* Vehicle & Customer Card */}
      <Card className="shadow-sm" styles={{ body: { padding: isMobile ? '14px' : '16px' } }} style={{ overflow: 'hidden' }}>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', gap: isMobile ? 16 : 20 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              <div style={{ width: 32, height: 32, borderRadius: 6, backgroundColor: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <CarOutlined style={{ fontSize: 16, color: '#3b82f6' }} />
              </div>
              <Text strong style={{ fontSize: 13 }}>Vehicle</Text>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#111827', wordWrap: 'break-word', minHeight: 48, padding: '8px 0' }}>
                {assetLabel(workOrder.vehicle)}
              </div>
              <div style={{ fontSize: 12, color: '#6b7280', wordWrap: 'break-word' }}>
                {workOrder.vehicle?.asset_type === 'equipment'
                  ? `Serial: ${workOrder.vehicle?.serial_number || 'N/A'}`
                  : `VIN: ${workOrder.vehicle?.vin || 'N/A'}`}
              </div>
              <div style={{ fontSize: 12, color: '#6b7280' }}>
                Mileage: {workOrder.vehicle?.current_mileage?.toLocaleString() || 'N/A'}
              </div>
            </div>
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              <div style={{ width: 32, height: 32, borderRadius: 6, backgroundColor: '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <UserOutlined style={{ fontSize: 16, color: '#22c55e' }} />
              </div>
              <Text strong style={{ fontSize: 13 }}>Customer</Text>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#111827', wordWrap: 'break-word', minHeight: 48, padding: '8px 0' }}>
                {workOrder.customer?.company_name ||
                  `${workOrder.customer?.first_name} ${workOrder.customer?.last_name}`}
              </div>
              <div style={{ fontSize: 12, color: '#6b7280', display: 'flex', alignItems: 'center', gap: 4, wordWrap: 'break-word' }}>
                <PhoneOutlined /> {workOrder.customer?.phone}
              </div>
              {workOrder.customer?.email && (
                <div style={{ fontSize: 12, color: '#6b7280', wordWrap: 'break-word', overflow: 'hidden', textOverflow: 'ellipsis' }}>{workOrder.customer.email}</div>
              )}
            </div>
          </div>
        </div>

        {/* Condition photos (odometer camera + photo strip) */}
        {workOrder.id && (
          <>
            <Divider style={{ margin: isMobile ? '10px 0' : '12px 0' }} />
            <div>
              <Text type="secondary" style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: 4 }}>
                Odometer / Condition Photos
              </Text>
              <PhotoStrip
                entityType="work_order"
                entityId={workOrder.id}
                subType="condition"
                editable={!isLocked}
                reloadKey={conditionReloadKey}
              />
              {!isLocked && (
                <CameraCaptureField
                  // 'condition' so the upload's sub_type matches the strip's
                  // filter above — purpose="odometer" stored sub_type "odometer"
                  // and the photo never appeared in this condition strip.
                  purpose="condition"
                  entityType="work_order"
                  entityId={workOrder.id}
                  onUploaded={() => setConditionReloadKey((k) => k + 1)}
                />
              )}
            </div>
          </>
        )}

        {/* Tax exemption badge — renders only when customer is tax-exempt */}
        {workOrder.customer && onTaxRateChange && (
          <TaxExemptionToggle
            customer={{
              id: workOrder.customer.id!,
              is_tax_exempt: (workOrder.customer as any).is_tax_exempt ?? false,
              tax_exempt_id: (workOrder.customer as any).tax_exempt_id ?? null,
            }}
            onTaxRateChange={onTaxRateChange}
          />
        )}

        {/* Problem Description - inline editable */}
        <Divider style={{ margin: isMobile ? '14px 0' : '16px 0' }} />
        <InlineEditField
          value={workOrder.problem_description || ''}
          onSave={handleFieldSave('problem_description')}
          label="Customer Complaint"
          isLocked={isLocked}
          multiline
          setIsEditing={setIsEditing}
        />

        {/* Internal notes — below complaint, auto-saves on blur */}
        <div style={{ marginTop: 8 }} onBlur={handleInternalNotesBlur}>
          <InternalNotesField
            value={internalNotes}
            onChange={(v) => { setInternalNotes(v); setIsEditing(true); }}
            rows={2}
          />
        </div>
      </Card>

      {/* Assignment Card - inline editable */}
      <Card
        title={<span style={{ fontSize: 13 }}>Assignment</span>}
        className="shadow-sm"
        styles={{ body: { padding: isMobile ? '14px' : '16px' } }}
        style={{ overflow: 'hidden' }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <InlineEditField
            value={workOrder.assigned_advisor || ''}
            onSave={handleFieldSave('assigned_advisor')}
            label="Advisor"
            isLocked={isLocked}
            setIsEditing={setIsEditing}
          />
          <div style={{ minHeight: 48, padding: '8px 0' }}>
            <Text style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#9ca3af', display: 'block', marginBottom: 4 }}>
              Technician
            </Text>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
              <ToolOutlined style={{ color: '#3b82f6' }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <TechnicianSelect
                  value={workOrder.assigned_technician_id ?? null}
                  legacyName={workOrder.assigned_technician || null}
                  disabled={isLocked}
                  size={isMobile ? 'large' : 'middle'}
                  placeholder="Unassigned"
                  onChange={handleTechnicianChange}
                />
              </div>
            </div>
            {bookPromptTech && (
              <Alert
                type="info"
                showIcon
                message={`Book a visit with ${bookPromptTech.name}?`}
                action={
                  <Space>
                    <Button size="small" type="primary" onClick={() => setBookingOpen(true)}>
                      Book
                    </Button>
                    <Button size="small" onClick={() => setBookPromptTech(null)}>
                      Not now
                    </Button>
                  </Space>
                }
                style={{ marginTop: 8 }}
              />
            )}
          </div>
        </div>
      </Card>

      {workOrder.id != null && (
        <QuickBookingDrawer
          visible={bookingOpen}
          workOrderId={workOrder.id}
          preferredTechnicianId={bookPromptTech?.id}
          onClose={() => setBookingOpen(false)}
          onBooked={() => { setBookingOpen(false); setBookPromptTech(null); }}
        />
      )}
    </div>
  );
};

export default memo(WorkOrderFormFields);
